import {useState} from 'react'

import {isEmpty} from 'es-toolkit/compat'
import {FormikErrors, useFormik} from 'formik'

import {useAppDispatch} from '../../hooks/redux'
import {signOut} from '../../redux/features/auth'
import {closeModal} from '../../redux/features/modal'

import useKeyPress from '../../../../commons/src/hooks/use-key-press'
import * as validator from '../../../../commons/src/libs/validator'
import * as authService from '../../../../commons/src/services/auth'
import preventDoubleClick from '../../../../commons/src/services/prevent-double-click'

import {ModalMeta} from '../../types'

import Button from '../../../../commons/src/components/button'
import FieldLabel from '../../../../commons/src/components/field-label/FieldLabel'
import FieldNote from '../../../../commons/src/components/field-note'
import FormRow from '../../../../commons/src/components/form-row'
import Headline from '../../../../commons/src/components/headline'
import LinkButton from '../../../../commons/src/components/link-button'
import List from '../../../../commons/src/components/list'
import Modal from '../../../../commons/src/components/modal'
import Paragraph from '../../../../commons/src/components/paragraph'
import PasswordInputField from '../../../../commons/src/components/password-input-field'
import SuccessMessage from '../../../../commons/src/components/success-message'
import Tooltip from '../../../../commons/src/components/tooltip'
import TooltipHandler from '../../../../commons/src/components/tooltip-handler'

type Props = {
  meta: ModalMeta
}

const AuthChangePasswordModal = ({meta}: Props) => {
  const dispatch = useAppDispatch()
  const [isDone, setIsDone] = useState(false)

  const formik = useFormik({
    initialValues: {
      password: '',
      confirmPassword: '',
      previousPassword: '',
      // Additional errors
      general: false,
    },
    validate: values => {
      const errors: FormikErrors<typeof values> = {}

      if (!validator.required(values.previousPassword)) {
        errors.previousPassword = 'true'
      }

      if (!validator.required(values.password)) {
        errors.password = 'true'
      }

      if (!validator.required(values.confirmPassword)) {
        errors.confirmPassword = 'true'
      }

      return errors
    },
    onSubmit: async (values, actions) => {
      if (values.password !== values.confirmPassword) {
        actions.setFieldError('general', 'Please make sure the provided new passwords are equal.')
        actions.setFieldError('password', 'true')
        actions.setFieldError('confirmPassword', 'true')
        return Promise.resolve()
      }

      return authService
        .changePassword(values.previousPassword, values.password)
        .then(() => {
          setIsDone(true)
          dispatch(signOut(true))
        })
        .catch(error => {
          if (
            error.message.match(/previousPassword/gi) &&
            error.name === 'InvalidParameterException'
          ) {
            actions.setFieldError('previousPassword', 'Your previous password is incorrect.')
          } else if (
            (error.name === 'InvalidParameterException' ||
              error.name === 'InvalidPasswordException') &&
            error.message.match(/password/gi)
          ) {
            actions.setFieldError('password', 'true')
            actions.setFieldError('confirmPassword', 'true')
            actions.setFieldError(
              'general',
              'Your password must have a minimum length of 8 characters and contain a combination of upper-case, lower-case and digits.'
            )
          } else {
            actions.setFieldError('general', error.message)
          }
        })
    },
  })

  useKeyPress(
    preventDoubleClick(() => formik.handleSubmit()),
    'Enter'
  )

  return (
    <Modal
      size="s"
      fullscreenMobile={meta.fullscreenMobile}
      headline={<Headline label="Change password" size="l" />}
      onClose={() => dispatch(closeModal())}
    >
      {!isDone && (
        <form onSubmit={formik.handleSubmit}>
          {formik.errors.general && <Paragraph error>{formik.errors.general}</Paragraph>}
          <FormRow>
            <FieldLabel label="Previous password">
              <PasswordInputField
                name="previousPassword"
                autoComplete="off"
                autoFocus
                appearance={
                  formik.submitCount > 0 && !!formik.errors.previousPassword ? 'error' : undefined
                }
                onChange={(v, n, event) => formik.handleChange(event)}
                value={formik.values.previousPassword}
                onBlur={formik.handleBlur}
                disabled={formik.isSubmitting}
              />
            </FieldLabel>
          </FormRow>
          <FormRow>
            <FieldLabel label="New password">
              <FieldNote
                note={
                  <TooltipHandler
                    openOnClick
                    tooltip={
                      <Tooltip
                        footer={
                          <List>
                            <li>
                              have a minimum <strong>length of 8 characters</strong>
                            </li>
                            <li>
                              contain at least <strong>one digit</strong>
                            </li>
                            <li>
                              contain at least <strong>one upper-case character</strong>
                            </li>
                            <li>
                              contain at least <strong>one lower-case character</strong>
                            </li>
                          </List>
                        }
                      >
                        Your password must…
                      </Tooltip>
                    }
                  >
                    <LinkButton>Show password requirements</LinkButton>
                  </TooltipHandler>
                }
              >
                <PasswordInputField
                  name="password"
                  autoComplete="new-password"
                  appearance={
                    formik.submitCount > 0 && !!formik.errors.password ? 'error' : undefined
                  }
                  onChange={(v, n, event) => formik.handleChange(event)}
                  value={formik.values.password}
                  onBlur={formik.handleBlur}
                  disabled={formik.isSubmitting}
                />
              </FieldNote>
            </FieldLabel>
          </FormRow>
          <FormRow>
            <FieldLabel label="Repeat password">
              <PasswordInputField
                name="confirmPassword"
                autoComplete="off"
                appearance={
                  formik.submitCount > 0 && !!formik.errors.confirmPassword ? 'error' : undefined
                }
                onChange={(v, n, event) => formik.handleChange(event)}
                value={formik.values.confirmPassword}
                onBlur={formik.handleBlur}
                disabled={formik.isSubmitting}
              />
            </FieldLabel>
          </FormRow>
          <Paragraph minor size="s">
            After changing your password you will be signed out from all your devices and you will
            have to sign in again.
          </Paragraph>
          <Button
            block
            disabled={formik.isSubmitting || (formik.submitCount > 0 && !isEmpty(formik.errors))}
            onClick={preventDoubleClick(() => formik.handleSubmit())}
          >
            Continue
          </Button>
        </form>
      )}
      {isDone && (
        <SuccessMessage>
          You have successfully updated your password! Please sign in again.
        </SuccessMessage>
      )}
    </Modal>
  )
}

export default AuthChangePasswordModal
